import React, { Children, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

function flattenOptions(children) {
  const output = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === React.Fragment) {
      output.push(...flattenOptions(child.props.children));
      return;
    }
    if (child.type === "option") {
      output.push({
        value: child.props.value ?? "",
        label: child.props.children,
        disabled: Boolean(child.props.disabled),
      });
      return;
    }
    if (child.props?.children) output.push(...flattenOptions(child.props.children));
  });
  return output;
}

export default function CybeSelect({ value, onChange, children, disabled = false, className = "", name, id, title, "aria-label": ariaLabel }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);
  const options = useMemo(() => flattenOptions(children), [children]);
  const selectedIndex = options.findIndex((option) => String(option.value) === String(value ?? ""));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : options[0];

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const desiredHeight = Math.min(220, Math.max(38, options.length * 34 + 8));
    const below = Math.max(0, window.innerHeight - rect.bottom);
    const above = Math.max(0, rect.top);
    const openUp = below < desiredHeight + 12 && above > below;
    const available = Math.max(96, (openUp ? above : below) - 14);
    const menuHeight = Math.min(desiredHeight, available);
    const width = Math.max(rect.width, 180);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    const top = openUp ? Math.max(8, rect.top - menuHeight - 6) : Math.min(window.innerHeight - menuHeight - 8, rect.bottom + 6);
    setPosition({ left, width, top, menuHeight });
  }, [options.length]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const handleOutside = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleViewport = () => updatePosition();
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", handleViewport);
    window.addEventListener("scroll", handleViewport, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", handleViewport);
      window.removeEventListener("scroll", handleViewport, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : options.findIndex((option) => !option.disabled));
  }, [open, selectedIndex, options]);

  function choose(option) {
    if (option.disabled) return;
    onChange?.({ target: { value: option.value, name } });
    setOpen(false);
    buttonRef.current?.focus();
  }

  function move(step) {
    if (!options.length) return;
    let index = activeIndex < 0 ? 0 : activeIndex;
    for (let i = 0; i < options.length; i += 1) {
      index = (index + step + options.length) % options.length;
      if (!options[index].disabled) {
        setActiveIndex(index);
        break;
      }
    }
  }

  function handleKeyDown(event) {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) setOpen(true); else move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) setOpen(true); else move(-1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!open) setOpen(true);
      else if (activeIndex >= 0) choose(options[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    } else if (event.key === "Home" && open) {
      event.preventDefault();
      setActiveIndex(options.findIndex((option) => !option.disabled));
    } else if (event.key === "End" && open) {
      event.preventDefault();
      for (let i = options.length - 1; i >= 0; i -= 1) if (!options[i].disabled) { setActiveIndex(i); break; }
    }
  }

  const menu = open && position && createPortal(
    <div ref={menuRef} className="cybe-select-menu" style={{ position: "fixed", left: position.left, top: position.top, width: position.width, zIndex: 9999 }} role="listbox" aria-label={ariaLabel || title || name}>
      <div className="cybe-select-menu-inner" style={{ maxHeight: position.menuHeight }}>
        {options.map((option, index) => {
          const isSelected = String(option.value) === String(value ?? "");
          const isActive = index === activeIndex;
          return (
            <button key={`${String(option.value)}-${index}`} type="button" role="option" aria-selected={isSelected} disabled={option.disabled} onMouseEnter={() => !option.disabled && setActiveIndex(index)} onClick={() => choose(option)} className={`cybe-select-option ${isSelected ? "is-selected" : ""} ${isActive ? "is-active" : ""}`}>
              <span className="cybe-select-option-label">{option.label}</span>
              {isSelected && <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <button ref={buttonRef} id={id} type="button" disabled={disabled} title={title} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} onClick={() => !disabled && setOpen((current) => !current)} onKeyDown={handleKeyDown} className={`cybe-select-trigger ${open ? "is-open" : ""} ${className}`}>
        <span className="cybe-select-value">{selected?.label ?? "Select"}</span>
        <ChevronDown className={`cybe-select-chevron h-4 w-4 shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {menu}
    </>
  );
}
